import { AssignationLimitStatus, AssignationType } from "@models/ISchema";
import { AdaptedCourseTypeAssignment } from "@services/types/settings/settingsTypes";

export type AdaptedAssignmentsByCourseType = {
  [courseType: string]: { [assignationType: string]: AdaptedCourseTypeAssignment };
};

/** Why the "Tipo asignación" selector of a link is disabled by the assignation policy. */
export type AssignationSelectorDisabledReason =
  | "course-type"
  | "invalid-udc"
  | "invalid-configuration";

/**
 * Statuses that block assigning roles in a whole link, by priority. The backend
 * resolves them per link (an invalid UdC gates every type of the course type),
 * so the first one found among the link's options applies to the link.
 */
const BLOCKING_LIMIT_STATUSES = [
  AssignationLimitStatus.InvalidConfiguration,
  AssignationLimitStatus.LinkUnavailable,
  AssignationLimitStatus.NotAllowedForCourse,
  AssignationLimitStatus.InvalidUdc,
];

const DISABLED_REASON_BY_STATUS: Partial<
  Record<AssignationLimitStatus, AssignationSelectorDisabledReason>
> = {
  [AssignationLimitStatus.InvalidConfiguration]: "invalid-configuration",
  [AssignationLimitStatus.LinkUnavailable]: "invalid-configuration",
  [AssignationLimitStatus.NotAllowedForCourse]: "course-type",
  [AssignationLimitStatus.InvalidUdc]: "invalid-udc",
};

export const ASSIGNATION_DISABLED_REASON_KEYS: Record<AssignationSelectorDisabledReason, string> = {
  "course-type": "bundle.link-card.instructor-table.body.rol-selector.disabled.course-type",
  "invalid-udc": "bundle.link-card.instructor-table.body.rol-selector.disabled.invalid-udc",
  "invalid-configuration":
    "bundle.link-card.instructor-table.body.rol-selector.disabled.invalid-configuration",
};

/** Role-agnostic course type explanation for institutions outside the UdC limit mode (HU451). */
const COURSE_TYPE_GENERIC_KEY =
  "bundle.link-card.instructor-table.body.rol-selector.disabled.course-type-generic";

/**
 * Setting rule for an assignation type in a course type (both matched in
 * lowercase, as `setRuleCourseTypeSetting` stores them), or null when none.
 */
export const getAssignationLimitRule = (
  adaptedAssignments: AdaptedAssignmentsByCourseType | undefined,
  courseType?: string | null,
  assignationTypeCode?: string | null,
): AdaptedCourseTypeAssignment | null => {
  if (!courseType || !assignationTypeCode) return null;

  return (
    adaptedAssignments?.[courseType.toLowerCase()]?.[assignationTypeCode.toLowerCase()] ?? null
  );
};

/**
 * UdC threshold of a rule in UdC limit mode (`unlimited_from_udc`), or null
 * when the rule uses the fixed limit mode.
 */
export const getUdcLimitThreshold = (rule?: AdaptedCourseTypeAssignment | null): number | null => {
  const threshold = rule?.unlimited_from_udc;

  if (threshold === undefined || threshold === null) return null;

  const value = Number(threshold);
  return Number.isFinite(value) ? value : null;
};

/**
 * UdC threshold of the first UdC rule configured for a course type, or null.
 * Backend rejections do not name the assignation type, and the backend applies
 * the UdC mode to the whole course type, so the course type decides the wording.
 */
export const getCourseTypeUdcThreshold = (
  adaptedAssignments: AdaptedAssignmentsByCourseType | undefined,
  courseType?: string | null,
): number | null => {
  if (!courseType) return null;

  const rules = Object.values(adaptedAssignments?.[courseType.toLowerCase()] ?? {});

  for (const rule of rules) {
    const threshold = getUdcLimitThreshold(rule);
    if (threshold !== null) return threshold;
  }

  return null;
};

/**
 * True when any rule of the setting uses the UdC limit mode (`unlimited_from_udc`).
 * The HU597 explanations (TEC) only apply to institutions configured this way.
 */
export const isUdcLimitModeEnabled = (
  adaptedAssignments: AdaptedAssignmentsByCourseType | undefined,
): boolean =>
  Object.values(adaptedAssignments ?? {}).some(rules =>
    Object.values(rules ?? {}).some(rule => getUdcLimitThreshold(rule) !== null),
  );

/** First blocking status among the link's assignation types, or null when roles can be assigned. */
export const getLinkBlockingLimitStatus = (
  assignationTypes: Array<Pick<AssignationType, "limitStatus"> | null | undefined> = [],
): AssignationLimitStatus | null =>
  BLOCKING_LIMIT_STATUSES.find(status =>
    assignationTypes.some(assignationType => assignationType?.limitStatus === status),
  ) ?? null;

/** Reason to disable the link selector, or null when it stays enabled. */
export const getAssignationSelectorDisabledReason = ({
  isValidCourseType,
  blockingStatus,
}: {
  isValidCourseType: boolean;
  blockingStatus?: AssignationLimitStatus | null;
}): AssignationSelectorDisabledReason | null => {
  if (!isValidCourseType) return "course-type";

  return (blockingStatus && DISABLED_REASON_BY_STATUS[blockingStatus]) || null;
};

/**
 * Tooltip key that explains a disabled selector, or null to keep it disabled
 * without explanation. In UdC limit mode (TEC) every reason is explained with
 * the HU597 texts. Outside it, only the front course type validation is
 * explained, with a role-agnostic text (the role catalog is not limited to the
 * block coordinator); the policy statuses resolved by the backend keep the
 * selector disabled silently.
 */
export const getAssignationDisabledTooltipKey = ({
  reason,
  isValidCourseType,
  isUdcLimitMode,
}: {
  reason: AssignationSelectorDisabledReason | null;
  isValidCourseType: boolean;
  isUdcLimitMode: boolean;
}): string | null => {
  if (!reason) return null;
  if (isUdcLimitMode) return ASSIGNATION_DISABLED_REASON_KEYS[reason];

  return isValidCourseType ? null : COURSE_TYPE_GENERIC_KEY;
};
